"use client";
import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { wrapper } from './styles.js';
import { InputField } from '../InputField/InputField.web.js';
import { IconButton } from '../IconButton/IconButton.web.js';
import { IconSubtract } from '../icons/ui_control/IconSubtract.web.js';
import { IconAdd } from '../icons/ui_control/IconAdd.web.js';
import { useSmartControlled } from '../utils/hooks/useSmartControlled.js';
import { spacingNumerical } from '../constants/spacing.js';
import { px2rem, inPixels } from '../utils/helpers/style.js';

var _excluded = ["name", "color", "outline", "withEditBox", "disabled", "id", "inputRef", "onChange", "min", "minErrorFormatter", "max", "maxErrorFormatter", "commonErrorMessage", "step", "tight", "value", "inputProps", "subtractButtonAriaLabel", "addButtonAriaLabel", "aria-label"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), true).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var InputStepper = function InputStepper(_ref) {
  var name = _ref.name,
    color = _ref.color,
    _ref$outline = _ref.outline,
    outline = _ref$outline === undefined ? false : _ref$outline,
    _ref$withEditBox = _ref.withEditBox,
    editBox = _ref$withEditBox === undefined ? false : _ref$withEditBox,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    id = _ref.id,
    inputRef = _ref.inputRef,
    onChange = _ref.onChange,
    min = _ref.min,
    _ref$minErrorFormatte = _ref.minErrorFormatter,
    minErrorFormatter = _ref$minErrorFormatte === undefined ? function (min) {
      return "Min ".concat(min);
    } : _ref$minErrorFormatte,
    max = _ref.max,
    _ref$maxErrorFormatte = _ref.maxErrorFormatter,
    maxErrorFormatter = _ref$maxErrorFormatte === undefined ? function (max) {
      return "Max ".concat(max);
    } : _ref$maxErrorFormatte,
    _ref$commonErrorMessa = _ref.commonErrorMessage,
    commonErrorMessage = _ref$commonErrorMessa === undefined ? 'Check' : _ref$commonErrorMessa,
    _ref$step = _ref.step,
    step = _ref$step === undefined ? 1 : _ref$step,
    _ref$tight = _ref.tight,
    tight = _ref$tight === undefined ? false : _ref$tight,
    valueProp = _ref.value,
    inputProps = _ref.inputProps,
    _ref$subtractButtonAr = _ref.subtractButtonAriaLabel,
    subtractButtonAriaLabel = _ref$subtractButtonAr === undefined ? 'Subtract' : _ref$subtractButtonAr,
    _ref$addButtonAriaLab = _ref.addButtonAriaLabel,
    addButtonAriaLabel = _ref$addButtonAriaLab === undefined ? 'Add' : _ref$addButtonAriaLab,
    ariaLabel = _ref['aria-label'],
    props = _objectWithoutProperties(_ref, _excluded);
  var _useSmartControlled = useSmartControlled('0', valueProp, onChange),
    _useSmartControlled2 = _slicedToArray(_useSmartControlled, 2),
    value = _useSmartControlled2[0],
    handleChange = _useSmartControlled2[1];
  if (step <= 0) {
    throw new Error('Step cannot be 0 or negative');
  }
  if (min > max) {
    throw new Error("Minimum accepted value (".concat(min, ") cannot be greater than maximum value (").concat(max, ")"));
  }
  if ((max - min) % step !== 0) {
    throw new Error("The range between minimum and maxmimum (".concat(max - min, ") is not divisible by step (").concat(step, ")"));
  }
  var parsedValue = parseFloat(value);
  var error = isNaN(parsedValue) || parsedValue % step !== 0 || parsedValue < min || parsedValue > max;
  var errorMessage = parsedValue < min ? minErrorFormatter(min) : parsedValue > max ? maxErrorFormatter(max) : commonErrorMessage;
  var subtractDisabled = disabled || parsedValue <= min;
  var addDisabled = disabled || parsedValue >= max;
  var inputFieldWidthInPx = editBox ? tight ? 86 : 98 : tight ? 50 : 82;
  var buttonSizeInPx = spacingNumerical.xl;
  var buttonMarginInPx = spacingNumerical.xxs;
  var errorMessageWidthInPx = inputFieldWidthInPx + buttonSizeInPx + buttonMarginInPx;
  var handleSubtract = function handleSubtract() {
    var newValue;
    if (isNaN(parsedValue)) {
      if (min < 0 && max >= 0) {
        newValue = 0 - step;
      } else if (max < 0) {
        newValue = max;
      } else {
        newValue = min;
      }
    } else if (parsedValue > max) {
      newValue = max;
    } else if (parsedValue % step !== 0) {
      newValue = Math.floor(parsedValue / step) * step;
    } else {
      newValue = parsedValue - step;
    }
    handleChange(newValue.toString());
  };
  var handleAdd = function handleAdd() {
    var newValue;
    if (isNaN(parsedValue)) {
      if (min <= 0 && max > 0) {
        newValue = 0 + step;
      } else if (min > 0) {
        newValue = min;
      } else {
        newValue = max;
      }
    } else if (parsedValue < min) {
      newValue = min;
    } else if (parsedValue % step !== 0) {
      newValue = Math.ceil(parsedValue / step) * step;
    } else {
      newValue = parsedValue + step;
    }
    handleChange(newValue.toString());
  };
  return /*#__PURE__*/React.createElement(wrapper, _extends({
    "data-testid": "elemental-InputStepper-".concat(id || name),
    id: id || name
  }, props), /*#__PURE__*/React.createElement(IconButton, {
    "data-testid": "elemental-InputStepper-".concat(id || name, "-IconButton-subtract"),
    marginRight: editBox ? px2rem(buttonMarginInPx) : undefined,
    status: outline ? 'secondary' : 'primary',
    disabled: subtractDisabled,
    "aria-label": subtractButtonAriaLabel,
    "aria-disabled": subtractDisabled,
    size: inPixels(buttonSizeInPx),
    onClick: handleSubtract,
    icon: /*#__PURE__*/React.createElement(IconSubtract, null),
    color: color
  }), /*#__PURE__*/React.createElement(InputField, {
    "data-testid": "elemental-InputStepper-".concat(id || name, "-InputField"),
    labelStyle: "static",
    name: name,
    type: "number",
    readOnly: !editBox,
    width: px2rem(inputFieldWidthInPx),
    disabled: disabled,
    error: error,
    errorMessage: errorMessage,
    errorMessageProps: {
      width: px2rem(errorMessageWidthInPx)
    },
    color: color,
    inputRef: inputRef,
    inputProps: _objectSpread({
      min: min,
      step: step,
      max: max,
      'aria-label': ariaLabel || name,
      'aria-labelledby': undefined,
      'aria-valuenow': parsedValue,
      'aria-valuemin': min,
      'aria-valuemax': max,
      'aria-invalid': error,
      'aria-disabled': disabled,
      'aria-readonly': !editBox,
      style: {
        textAlign: 'center'
      }
    }, inputProps),
    onChange: function onChange(ev) {
      return handleChange(ev.target.value);
    },
    value: value
  }), /*#__PURE__*/React.createElement(IconButton, {
    "data-testid": "elemental-InputStepper-".concat(id || name, "-IconButton-add"),
    marginLeft: editBox ? px2rem(buttonMarginInPx) : undefined,
    status: outline ? 'secondary' : 'primary',
    disabled: addDisabled,
    "aria-label": addButtonAriaLabel,
    "aria-disabled": addDisabled,
    size: inPixels(buttonSizeInPx),
    onClick: handleAdd,
    icon: /*#__PURE__*/React.createElement(IconAdd, null),
    color: color
  }));
};

export { InputStepper };
